import React from 'react';
import { TFunction } from 'i18next';
import { FormAnswer, FormTemplateQuestionFieldOption } from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import getDocumentAnswerStatuses from '@lib/components/Table/commonComponents/DocumentResponseCell/getDocumentAnswerStatuses';
import {
  DocumentLockedMessage,
  DocumentPreviewOrUnavailable,
  DocumentReadChip,
  getDocumentIds,
} from '@lib/components/Table/commonComponents/DocumentResponseCell/DocumentResponseParts';
import { CellProps } from '@lib/components/Table/types';
import Typography from '@lib/components/Typography/Typography';
import useDocumentsMap from '@lib/utils/fetch/hooks/useDocumentsMap';
import { CompareCompletionRow } from './types';
import styles from './DocumentCompareCells.module.scss';

// Compare mode shows one row per question, side by side per completion — a
// document question therefore can't reuse `DocumentResponseCell` (built for
// a single completion's own column) since it renders the full doc preview +
// status + time for every column, repeating the (identical) doc identity
// once per compared user. Here the identity list is rendered once in its
// own shared column, and each completion's column only carries status/time.

type DocumentIdentityCellProps<T extends CompareCompletionRow> =
  CellProps<T> & {
    t: TFunction<'translation', undefined>;
    onDocumentClick?: (documentationId: string) => void;
    isViewLocked?: boolean;
    isFullAdmin?: boolean;
    onUpgradePlan?: () => void;
  };

export function DocumentIdentityCell<T extends CompareCompletionRow>(
  props: DocumentIdentityCellProps<T>,
) {
  const { item, t, onDocumentClick, isViewLocked, isFullAdmin, onUpgradePlan } =
    props;
  const options = (item.options || []) as FormTemplateQuestionFieldOption[];
  const docIds = getDocumentIds(options);
  const { documentsMap, loading } = useDocumentsMap(docIds);

  if (!docIds.length) {
    if (isViewLocked) {
      return (
        <DocumentLockedMessage
          t={t}
          isFullAdmin={isFullAdmin}
          onUpgradePlan={onUpgradePlan}
        />
      );
    }
    return <TextCell {...props} value="-" />;
  }

  if (loading) {
    return <CircleLoader />;
  }

  const sorted = [...options].sort((a, b) => a.position - b.position);

  return (
    <div className={styles.documentList}>
      {sorted.map((opt) => {
        const docId = opt.optionDocumentation?.documentationId;
        if (!docId) return null;
        return (
          <div key={opt.id} className={styles.documentIdentityRow}>
            <DocumentPreviewOrUnavailable
              t={t}
              doc={documentsMap.get(docId)}
              docId={docId}
              onDocumentClick={onDocumentClick}
            />
          </div>
        );
      })}
    </div>
  );
}

type DocumentStatusCellProps<T extends CompareCompletionRow> = CellProps<T> & {
  t: TFunction<'translation', undefined>;
  answer?: Pick<FormAnswer, 'resp' | 'documentationVersionViews'> | null;
};

export function DocumentStatusCell<T extends CompareCompletionRow>(
  props: DocumentStatusCellProps<T>,
) {
  const { item, t, answer } = props;
  const options = (item.options || []) as FormTemplateQuestionFieldOption[];
  const statuses = getDocumentAnswerStatuses(options, answer);

  if (!statuses.length) {
    return <TextCell {...props} value="-" />;
  }

  return (
    <div className={styles.documentList}>
      {statuses.map((status) => (
        <div key={status.option.id} className={styles.documentStatusRow}>
          <DocumentReadChip t={t} status={status} />
          {status.readingTime !== '-' && (
            <Typography variant="caption" className={styles.documentRowTime}>
              {status.readingTime}
            </Typography>
          )}
        </div>
      ))}
    </div>
  );
}
